import {
    AccessPlanFromBackend,
    CreateAccessResponse,
    InventorySnapshot,
} from '@/services/dtos/accessRequest.dto';
import { OperatorTypeEnum } from '@/services/dtos/enums/accessAutomation.enum';
import {
    AccessPlanSchema,
    CreateAccessResponseSchema,
    InventorySnapshotSchema,
} from '@/services/validation-schemas/create-access-response';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "components/ui/card";
import { Separator } from 'components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import {
    Briefcase,
    Building,
    Eye,
    EyeOff,
    KeySquare,
    User,
    Users,
} from 'lucide-react';
import { useState } from 'react';
import { ACCESS_SECTION_MESSAGES } from './access-section-messages';
import { BlockedAccessSection } from './blocked-access-section';
import { InventorySnapshotSection } from './inventory-snapshot-section';
import { ReusedAccessSection } from './reused-access-section';

// Definição de tipagem correta
interface CompletedConfigProps {
    accessDetails?: CreateAccessResponse | undefined | null;
}

export const CompletedConfig = ({ accessDetails }: CompletedConfigProps) => {
    return (
        <Card>
            <CardHeader className='relative'>
                <CardTitle>Configurações de Acesso</CardTitle>
                <CardDescription>
                    Configurações de acesso geradas na criação do acesso
                </CardDescription>
                <div className="gap-6 bg-base-100 p-2 rounded-md shadow-sm border absolute top-3 right-3">
                    <div className="space-y-2 flex items-center gap-2">
                        <span className='flex items-center gap-2'>
                            <KeySquare className='h-5 w-5 text-gray-500' />
                            <h3 className="text-lg font-medium2">
                                Tipo de Acesso
                            </h3>
                        </span>
                        <Badge variant="outline" className="text-base font-normal px-3 py-1 -translate-y-1">
                            {accessDetails?.accessType}
                        </Badge>
                    </div>
                </div>
            </CardHeader>


            <CardContent className="grid gap-4">
                <Details accessDetails={accessDetails} />
            </CardContent>
        </Card>
    );
};

interface DetailsProps {
    accessDetails?: CreateAccessResponse | undefined | null
}

const Details = ({ accessDetails }: DetailsProps) => {
    console.debug("Acessando detalhes de configuração:", accessDetails);

    const parsed = CreateAccessResponseSchema.safeParse(accessDetails);

    if (!parsed.success) {
        console.error("Erro de validação Zod:", parsed.error.format());
        return (
            <p className="text-red-500">
                Não foram encontradas configurações de acesso.
            </p>
        );
    }

    const validatedData = parsed.data as CreateAccessResponse;
    const { userResponsible, operators } = validatedData;

    // R-F2: parse SEPARADO do plan — não derruba a tela se shape inesperado
    const rawPlan = accessDetails?.plan;
    const planParseResult =
        rawPlan != null ? AccessPlanSchema.safeParse(rawPlan) : null;
    const plan = planParseResult?.success
        ? (planParseResult.data as AccessPlanFromBackend)
        : null;
    const planError = planParseResult != null && !planParseResult.success;

    if (planError) {
        console.error(
            "Erro de parse do plan (R-F2):",
            planParseResult!.error.format()
        );
    }

    // Snapshot — também parse separado
    const rawSnapshot = accessDetails?.inventorySnapshot;
    const snapshotParseResult =
        rawSnapshot != null ? InventorySnapshotSchema.safeParse(rawSnapshot) : null;
    const snapshot = snapshotParseResult?.success
        ? (snapshotParseResult.data as InventorySnapshot)
        : null;

    const bloqueadosTemAlgo =
        plan != null &&
        ((plan.users?.toBlock?.length ?? 0) > 0 ||
            (plan.operators?.toBlock?.length ?? 0) > 0);

    // Helper function to get a color based on operator type
    const getOperatorTypeColor = (type: OperatorTypeEnum) => {
        switch (type) {
            case OperatorTypeEnum.CASHIER:
                return "bg-red-100 text-red-800"
            case OperatorTypeEnum.MANAGER:
                return "bg-blue-100 text-blue-800"
            default:
                return "bg-gray-100 text-gray-800"
        }
    }

    return (
        <section aria-labelledby="user-permissions-heading" className="space-y-6">

            {/* Password and Access Type Section */}


            <Separator />


            {/* User Responsible Section */}
            <div className="space-y-4">
                <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-gray-500" />
                    <h3 className="text-lg font-medium">Operadores</h3>
                </div>
                <div className='grid grid-cols-3 gap-6'>
                    <PasswordDemo password={userResponsible.password} />
                </div>

                {userResponsible.accessesCreated.length > 0 ? (
                    <ResponsiveTable>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Código do Operador</TableHead>
                                <TableHead>ID de Departamento</TableHead>
                                <TableHead>ID de Tipo de Funcionário</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {userResponsible.accessesCreated.map((user, index) => (
                                <TableRow key={`user-${index}`}>
                                    <TableCell className="font-medium">{user.usercode}</TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-1">
                                            <Building className="h-4 w-4 text-gray-500" />
                                            <span>{user.departmentId}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-1">
                                            <Briefcase className="h-4 w-4 text-gray-500" />
                                            <span>{user.employeeTypeId}</span>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </ResponsiveTable>
                ) : (
                    <p className="text-sm text-gray-500 italic">Nenhum usuário responsável encontrado.</p>
                )}
            </div>
            <Separator />
            {/* Operators Section */}
            <div className="space-y-4">

                <div className="flex items-center gap-2">
                    <User className="h-5 w-5 text-gray-500" />
                    <h3 className="text-lg font-medium">Usuários Responsáveis</h3>
                </div>
                <div className='grid grid-cols-3 gap-6'>
                    <PasswordDemo password={operators.password} />
                </div>
                {operators.accessesCreated.length > 0 ? (
                    <ResponsiveTable>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Código do Responsável</TableHead>
                                <TableHead>Tipo</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {operators.accessesCreated.map((operator, index) => (
                                <TableRow key={`operator-${index}`}>
                                    <TableCell className="font-medium">{operator.usercode}</TableCell>
                                    <TableCell>
                                        <Badge className={getOperatorTypeColor(operator.type)}>{operator.type}</Badge>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </ResponsiveTable>
                ) : (
                    <p className="text-sm text-gray-500 italic">Nenhum operador encontrado.</p>
                )}
            </div>

            {/* NOVO — render condicional defensivo */}

            {/* R-F2: mensagem amigável se plan veio malformado */}
            {planError && (
                <>
                    <Separator />
                    <p className="text-sm text-amber-600 italic">
                        {ACCESS_SECTION_MESSAGES.PLAN_ERROR}
                    </p>
                </>
            )}

            {/* Reaproveitados — SEMPRE visível se plan existe */}
            {plan && (
                <>
                    <Separator />
                    <ReusedAccessSection
                        users={plan.users?.toKeep ?? []}
                        operators={plan.operators?.toKeep ?? []}
                    />
                </>
            )}

            {/* Bloqueados — OCULTA se vazio */}
            {plan && bloqueadosTemAlgo && (
                <>
                    <Separator />
                    <BlockedAccessSection
                        users={plan.users?.toBlock ?? []}
                        operators={plan.operators?.toBlock ?? []}
                    />
                </>
            )}

            {/* Snapshot — SEMPRE visível se existe */}
            {snapshot && (
                <>
                    <Separator />
                    <InventorySnapshotSection
                        snapshot={snapshot}
                        planSource={plan?.source}
                    />
                </>
            )}

        </section>
    )
}


function PasswordDemo({ password }: { password: string }) {
    const [showPassword, setShowPassword] = useState(false)

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword)
    }

    return (
        <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-500">Senha</h3>
            <div className="flex items-center space-x-2">
                <div className="relative flex-1">
                    <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        readOnly
                        onCopy={(e) => e.preventDefault()}
                        className="w-full px-3 py-2 border rounded-md bg-gray-50 pointer-events-none"
                    />
                </div>
                <Button
                    variant="outline"
                    size="icon"
                    onClick={togglePasswordVisibility}
                    aria-label={showPassword ? "Esconder senha" : "Mostrar senha"}
                >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
            </div>
        </div>
    )
}


function ResponsiveTable({ children }: { children: React.ReactNode }) {
    return (
        <div className="overflow-x-auto rounded-lg border">
            <Table className="min-w-[500px]">{children}</Table>
        </div>
    );
}

export default Details
